import { useState, useEffect, useMemo, useRef } from "react";
import { ArrowLeft, Percent, DollarSign, Banknote, CreditCard, Smartphone, Plus, Trash2, Users, Ticket, Minus, Loader2, CheckCircle2, MessageSquare, ShoppingBag, AlertTriangle, Receipt, XCircle, RefreshCw, WifiOff, FileText, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

import { ScrollArea } from "@/components/ui/scroll-area";
import { NumericKeypadProvider } from "@/components/numeric-keypad/NumericKeypadProvider";
import { NumericKeypadDock } from "@/components/numeric-keypad/NumericKeypadDock";
import { useNumericKeypadField } from "@/components/numeric-keypad/useNumericKeypadField";
import { centsToFixedString, toCentsFromString, toCentsFromNumber } from "@/lib/currency-cents";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

function getContrastColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}
import type { OrderInfo, OrderType, CartItem } from "@/hooks/useCart";
import { usePaymentMethods, PaymentMethod, PaymentMethodType } from "@/hooks/usePaymentMethods";
import { useServiceTax } from "@/hooks/useServiceTax";
import { useCouvert } from "@/hooks/useCouvert";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { usePeopleCountSettings } from "@/hooks/usePeopleCountSettings";
import { usePartialPayments } from "@/hooks/usePartialPayments";
import { usePartialPaymentSettings } from "@/hooks/usePartialPaymentSettings";
import { PartialPaymentDialog } from "./PartialPaymentDialog";
import type { PaymentEntry } from "./CheckoutDialog";
import type { SentOrderItem, CancelledOrderItem } from "@/hooks/useCurrentOrderItems";
import { printConferenceBill } from "@/lib/order-printing";
import { useFiscalConfig } from "@/hooks/useFiscalConfig";
import { useSQLiteSync } from "@/hooks/useSQLiteSync";
import { CalculatorDialog } from "@/components/ui/calculator-dialog";

interface CommandEntry {
  id: string;
  command_number: string;
  entry_type_name: string;
  amount: number;
  entry_mode: "fixed" | "minimum_consumption";
  status: string;
}

type DiscountType = "percentage" | "fixed";

interface CheckoutScreenProps {
  orderInfo: OrderInfo;
  onOrderInfoChange: (info: OrderInfo) => void;
  subtotal: number;
  itemCount: number;
  items: CartItem[];
  sentItems: SentOrderItem[];
  cancelledItems?: CancelledOrderItem[];
  onConfirm: (discount: number, payments: PaymentEntry[], serviceTax: number, couvertQuantity: number, couvertAmount: number, tableId?: string, commandId?: string, entryDifference?: number, entryId?: string, peopleCount?: number, onOrderCreated?: (orderId: string) => void, subtotal?: number) => void;
  onBack: () => void;
  isSubmitting: boolean;
  destinationLabel: string;
  tableId?: string;
  commandId?: string;
  orderId?: string;
  orderNumber?: number;
  onUpdateCartQuantity?: (productId: string, delta: number) => void;
  onDecreaseItemQuantity?: (itemId: string, quantity: number) => Promise<boolean>;
  onIncreaseItemQuantity?: (item: SentOrderItem) => void;
  fetchError?: string | null;
  onRetryFetch?: () => void;
  onNfceEmission?: (orderId: string, payments: PaymentEntry[]) => void;
}

const paymentTypeIcons: Record<PaymentMethodType, React.ReactNode> = {
  cash: <Banknote className="h-5 w-5" />,
  card: <CreditCard className="h-5 w-5" />,
  digital_wallet: <Smartphone className="h-5 w-5" />,
};

export function CheckoutScreen({
  orderInfo,
  onOrderInfoChange,
  subtotal,
  itemCount,
  items,
  sentItems,
  cancelledItems,
  onConfirm,
  onBack,
  isSubmitting,
  destinationLabel,
  tableId,
  commandId,
  orderId,
  orderNumber,
  onUpdateCartQuantity,
  onDecreaseItemQuantity,
  onIncreaseItemQuantity,
  fetchError,
  onRetryFetch,
  onNfceEmission,
}: CheckoutScreenProps) {
  return (
    <NumericKeypadProvider>
      <CheckoutScreenInner
        orderInfo={orderInfo}
        onOrderInfoChange={onOrderInfoChange}
        subtotal={subtotal}
        itemCount={itemCount}
        items={items}
        sentItems={sentItems}
        cancelledItems={cancelledItems}
        onConfirm={onConfirm}
        onBack={onBack}
        isSubmitting={isSubmitting}
        destinationLabel={destinationLabel}
        tableId={tableId}
        commandId={commandId}
        orderId={orderId}
        orderNumber={orderNumber}
        onUpdateCartQuantity={onUpdateCartQuantity}
        onDecreaseItemQuantity={onDecreaseItemQuantity}
        onIncreaseItemQuantity={onIncreaseItemQuantity}
        fetchError={fetchError}
        onRetryFetch={onRetryFetch}
        onNfceEmission={onNfceEmission}
      />
    </NumericKeypadProvider>
  );
}

function CheckoutScreenInner({
  orderInfo,
  onOrderInfoChange,
  subtotal,
  itemCount,
  items,
  sentItems,
  cancelledItems,
  onConfirm,
  onBack,
  isSubmitting,
  destinationLabel,
  tableId,
  commandId,
  orderId,
  orderNumber,
  onUpdateCartQuantity,
  onDecreaseItemQuantity,
  onIncreaseItemQuantity,
  fetchError,
  onRetryFetch,
  onNfceEmission,
}: CheckoutScreenProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [discountType, setDiscountType] = useState<DiscountType>("percentage");
  const [discountValue, setDiscountValue] = useState<string>("");
  // Split payment sempre ativo
  const isSplitPayment = true;
  const [applyServiceTax, setApplyServiceTax] = useState(true);
  const [applyCouvert, setApplyCouvert] = useState(true);
  const [couvertQuantity, setCouvertQuantity] = useState<number>(0);
  const [customServiceTaxPercentage, setCustomServiceTaxPercentage] = useState<string>("");
  
  
  // Split payment mode
  const [payments, setPayments] = useState<PaymentEntry[]>([]);
  const [newPaymentMethod, setNewPaymentMethod] = useState<PaymentMethod | null>(null);
  const [newPaymentAmount, setNewPaymentAmount] = useState<string>("");
  const paymentAmountInputRef = useRef<HTMLInputElement | null>(null);
  const [isPrintingConference, setIsPrintingConference] = useState(false);
  
  // Partial payment state
  const [partialPaymentDialogOpen, setPartialPaymentDialogOpen] = useState(false);
  
  const { getActivePaymentMethods, isLoading: isLoadingPayments } = usePaymentMethods();
  const activePaymentMethods = getActivePaymentMethods();
  
  const { serviceTaxPercentage, serviceTaxEnabled, calculateServiceTax } = useServiceTax();
  const { couvertEnabled, couvertPrice, calculateCouvert } = useCouvert();
  const { activeRestaurant } = useRestaurantContext();
  const { peopleCountCheckoutEnabled } = usePeopleCountSettings();
  
  // Partial payments
  const { isPartialPaymentEnabled } = usePartialPaymentSettings();
  const { partialPayments, totalPaid: partialTotalPaid, refetch: refetchPartialPayments } = usePartialPayments(orderId || null);

  // NFC-e - check if fiscal is configured (modals are at PDVPage level)
  const { config: fiscalConfig } = useFiscalConfig();
  const { isOnline: isSQLiteOnline } = useSQLiteSync();

  // People count state
  const [peopleCount, setPeopleCount] = useState<number>(0);
  
  // State for command entry
  const [commandEntry, setCommandEntry] = useState<CommandEntry | null>(null);
  const [isLoadingEntry, setIsLoadingEntry] = useState(false);
  
  // State for decrease confirmation dialog
  const [decreaseConfirmDialog, setDecreaseConfirmDialog] = useState<{
    open: boolean;
    item: SentOrderItem | null;
  }>({ open: false, item: null });
  const [isDecreasing, setIsDecreasing] = useState(false);
  
  // Fetch command entry when command is selected
  useEffect(() => {
    const fetchCommandEntry = async () => {
      if (!commandId || !activeRestaurant || orderInfo.type !== "tab") {
        setCommandEntry(null);
        return;
      }
      
      setIsLoadingEntry(true);
      try {
        // Get command number from commandId
        const { data: commandData } = await supabase
          .from("commands")
          .select("number")
          .eq("id", commandId)
          .single();
        
        if (!commandData) {
          setCommandEntry(null);
          return;
        }
        
        const { data, error } = await supabase
          .from("command_entries")
          .select("*")
          .eq("restaurant_id", activeRestaurant.id)
          .eq("command_number", commandData.number)
          .eq("status", "active")
          .maybeSingle();
        
        if (error) throw error;
        setCommandEntry(data);
      } catch (err) {
        console.error("Error fetching command entry:", err);
        setCommandEntry(null);
      } finally {
        setIsLoadingEntry(false);
      }
    };
    
    fetchCommandEntry();
  }, [commandId, activeRestaurant, orderInfo.type]);
  
  // Calculate entry amount (covers both "fixed" and "minimum_consumption" modes)
  const entryAmount = useMemo(() => {
    if (!commandEntry) return 0;
    if (commandEntry.entry_mode === "fixed") {
      return Number(commandEntry.amount) || 0;
    }
    if (commandEntry.entry_mode === "minimum_consumption") {
      const minimumAmount = Number(commandEntry.amount) || 0;
      return subtotal >= minimumAmount ? 0 : minimumAmount - subtotal;
    }
    return 0;
  }, [commandEntry, subtotal]);

  // Initialize on mount
  useEffect(() => {
    setApplyServiceTax(serviceTaxEnabled);
    setApplyCouvert(couvertEnabled);
    setPeopleCount(orderInfo.peopleCount || 0);
    setCustomServiceTaxPercentage(serviceTaxPercentage.toString());
  }, []);

  // Set default payment method when loaded
  useEffect(() => {
    if (activePaymentMethods.length > 0 && !newPaymentMethod) {
      setNewPaymentMethod(activePaymentMethods[0]);
    }
  }, [activePaymentMethods, newPaymentMethod]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Calculate discount amount
  const discountAmount = (() => {
    const value = parseFloat(discountValue) || 0;
    if (discountType === "percentage") {
      return (subtotal * Math.min(value, 100)) / 100;
    }
    return Math.min(value, subtotal);
  })();

  // Calculate service tax with custom percentage
  const effectiveServiceTaxPercentage = parseFloat(customServiceTaxPercentage) || 0;
  const serviceTaxAmount = applyServiceTax && serviceTaxEnabled 
    ? ((subtotal - discountAmount) * effectiveServiceTaxPercentage) / 100 
    : 0;

  // Calculate couvert
  const couvertAmount = applyCouvert && couvertEnabled ? calculateCouvert(couvertQuantity) : 0;

  // Total includes entry fee (fixed or minimum-consumption difference)
  const grossTotal = subtotal - discountAmount + serviceTaxAmount + couvertAmount + entryAmount;
  
  // Total after partial payments
  const total = grossTotal - partialTotalPaid;
  
  // Amount remaining to pay (for final checkout)
  const amountToPayFinal = Math.max(0, total);

  // Trabalhar com centavos para evitar bugs de ponto flutuante
  const amountToPayFinalCents = toCentsFromNumber(amountToPayFinal);

  // Split payment calculations
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const totalPaidCents = payments.reduce((sum, p) => sum + toCentsFromNumber(p.amount), 0);
  const remainingCents = amountToPayFinalCents - totalPaidCents;
  const remainingAmount = remainingCents / 100;
  const changeCents = Math.max(0, -remainingCents);
  const changeAmount = changeCents / 100;

  const paymentQuickActions = useMemo(() => [
    {
      id: "remaining",
      label: "Restante",
      onPress: () => setNewPaymentAmount(centsToFixedString(Math.max(0, remainingCents))),
    },
  ], [remainingCents]);

  const paymentAmountField = useNumericKeypadField({
    id: "checkout.payment_amount",
    label: "Valor do pagamento",
    mode: "currency",
    getCents: () => toCentsFromString(newPaymentAmount),
    setCents: (c) => setNewPaymentAmount(centsToFixedString(c)),
    onClear: () => setNewPaymentAmount(""),
    maxCents: 999_999_99,
    quickActions: paymentQuickActions,
  });

  const discountFixedField = useNumericKeypadField({
    id: "checkout.discount_fixed",
    label: "Desconto (R$)",
    mode: "currency",
    getCents: () => toCentsFromString(discountValue),
    setCents: (c) => setDiscountValue(centsToFixedString(c)),
    onClear: () => setDiscountValue(""),
    maxCents: toCentsFromNumber(subtotal),
  });


  const handleAddPayment = () => {
    if (!newPaymentMethod) {
      toast.error("Selecione uma forma de pagamento");
      return;
    }

    const amountCents = toCentsFromString(newPaymentAmount);
    if (amountCents <= 0) {
      toast.error("Informe um valor maior que zero");
      return;
    }

    const maxAllowedCents = Math.max(0, remainingCents);
    const allowChange = newPaymentMethod.type === "cash";
    if (!allowChange && amountCents > maxAllowedCents) {
      toast.error("Valor maior que o restante (somente dinheiro permite troco)");
      return;
    }

    const amount = amountCents / 100;

    const feeAmount = newPaymentMethod.type === "card" && newPaymentMethod.fee_percentage > 0
      ? (amount * newPaymentMethod.fee_percentage) / 100
      : 0;

    const newEntry: PaymentEntry = {
      id: crypto.randomUUID(),
      paymentMethod: newPaymentMethod,
      amount,
      feeAmount,
    };

    setPayments([...payments, newEntry]);
    setNewPaymentAmount("");
    toast.success("Pagamento adicionado");
  };

  const canAddPayment = useMemo(() => {
    if (!newPaymentMethod) return false;
    const amountCents = toCentsFromString(newPaymentAmount);
    if (amountCents <= 0) return false;

    // Permitir troco em dinheiro.
    if (newPaymentMethod.type === "cash") return true;

    const maxAllowedCents = Math.max(0, remainingCents);
    return amountCents <= maxAllowedCents;
  }, [newPaymentMethod, newPaymentAmount, remainingCents]);

  const handleRemovePayment = (id: string) => {
    setPayments(payments.filter(p => p.id !== id));
  };

  const handleFillRemaining = () => {
    if (remainingCents > 0) {
      setNewPaymentAmount(centsToFixedString(remainingCents));
    }
  };

  const handleSelectPaymentMethod = (method: PaymentMethod) => {
    setNewPaymentMethod(method);
    // Preencher automaticamente com o restante (fluxo padrão)
    setNewPaymentAmount(centsToFixedString(Math.max(0, remainingCents)));
    // Ativar o campo do teclado numérico e exibir cursor no input
    paymentAmountField.activate();
    requestAnimationFrame(() => paymentAmountInputRef.current?.focus());
  };

  const handleReprintConference = async () => {
    if (!activeRestaurant?.id) return;
    setIsPrintingConference(true);
    try {
      await printConferenceBill(activeRestaurant.id, orderId, tableId, commandId);
    } finally {
      setIsPrintingConference(false);
    }
  };

  // Validação: valor zero ou pagamentos cobrem o total
  const isPaymentValid = amountToPayFinalCents <= 0
    ? true
    : payments.length > 0 && remainingCents <= 0;

  const handleConfirm = (onOrderCreated?: (orderId: string) => void) => {
    const finalCouvertQuantity = applyCouvert && couvertEnabled ? couvertQuantity : 0;
    const finalCouvertAmount = applyCouvert && couvertEnabled ? couvertAmount : 0;
    const finalEntryDifference = entryAmount > 0 ? entryAmount : undefined;
    const finalEntryId = commandEntry?.id;
    const finalPeopleCount = peopleCount > 0 ? peopleCount : undefined;
    
    // Caso especial: valor a pagar é zero ou negativo (ex: todos itens cancelados)
    if (amountToPayFinal <= 0) {
      onConfirm(discountAmount, [], serviceTaxAmount, finalCouvertQuantity, finalCouvertAmount, tableId, commandId, finalEntryDifference, finalEntryId, finalPeopleCount, onOrderCreated, subtotal);
      return;
    }
    
    onConfirm(discountAmount, payments, serviceTaxAmount, finalCouvertQuantity, finalCouvertAmount, tableId, commandId, finalEntryDifference, finalEntryId, finalPeopleCount, onOrderCreated, subtotal);
  };
  
  // Check if fiscal is configured
  const fiscalEnabled = fiscalConfig?.integrator === 'acbr_monitor';
  
  // Handle confirm with NFC-e emission - delegates to PDVPage via callback
  const handleConfirmWithNfce = () => {
    if (!onNfceEmission) return;
    
    handleConfirm((newOrderId: string) => {
      // After order is created, notify PDVPage to handle NFC-e flow
      onNfceEmission(newOrderId, payments);
    });
  };

  // Handle decrease request - show confirmation dialog
  const handleDecreaseRequest = (item: SentOrderItem) => {
    setDecreaseConfirmDialog({ open: true, item });
  };

  // Confirm decrease
  const handleConfirmDecrease = async () => {
    if (!decreaseConfirmDialog.item || !onDecreaseItemQuantity) return;
    
    setIsDecreasing(true);
    try {
      await onDecreaseItemQuantity(decreaseConfirmDialog.item.id, 1);
    } finally {
      setIsDecreasing(false);
      setDecreaseConfirmDialog({ open: false, item: null });
    }
  };

  // Handle retry fetch
  const handleRetryFetch = async () => {
    if (!onRetryFetch) return;
    setIsRetrying(true);
    try {
      await onRetryFetch();
    } finally {
      setIsRetrying(false);
    }
  };


  return (
      <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Connection Error Banner */}
      {fetchError && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center gap-3">
          <WifiOff className="h-4 w-4 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-700">Conexão instável</p>
            <p className="text-xs text-amber-600">Mostrando últimos itens carregados. Verifique a conexão.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRetryFetch}
            disabled={isRetrying}
            className="shrink-0 border-amber-500/50 text-amber-700 hover:bg-amber-500/10"
          >
            {isRetrying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            Recarregar
          </Button>
        </div>
      )}
      {/* Offline Mode Banner */}
      {!isSQLiteOnline && !fetchError && (
        <div className="bg-blue-500/10 border-b border-blue-500/30 px-4 py-2 flex items-center gap-3">
          <WifiOff className="h-4 w-4 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Modo Offline</p>
            <p className="text-xs text-blue-600 dark:text-blue-500">A conta será fechada localmente e sincronizada quando a conexão voltar.</p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-card">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold">Receber Conta</h1>
          <p className="text-sm text-muted-foreground truncate">{destinationLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <CalculatorDialog />
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={handleReprintConference}
            disabled={isSubmitting || isPrintingConference}
          >
            {isPrintingConference ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Receipt className="h-4 w-4" />
            )}
            Reimprimir Conferência
          </Button>
        </div>
      </div>

      {/* Main Content - Three Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Column - Items List */}
        <div className="w-72 lg:w-80 border-r flex flex-col bg-muted/30">
          <div className="p-3 border-b bg-card">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ShoppingBag className="h-4 w-4" />
              Itens do Pedido ({itemCount})
            </div>
          </div>
          <ScrollArea className="flex-1">
            <div className="divide-y">
              {/* Sent Items */}
              {sentItems.length > 0 && (
                <>
                  <div className="px-3 py-2 bg-muted/50">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      Enviados ({sentItems.length})
                    </div>
                  </div>
                  {sentItems.map((item) => (
                    <div key={item.id} className="p-3 bg-muted/20">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.product_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {onDecreaseItemQuantity && onIncreaseItemQuantity && (
                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => handleDecreaseRequest(item)}
                                  disabled={isSubmitting || isDecreasing}
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                                <span className="w-6 text-center text-sm font-medium">
                                  {item.quantity}
                                </span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => onIncreaseItemQuantity(item)}
                                  disabled={isSubmitting}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                            {(!onDecreaseItemQuantity || !onIncreaseItemQuantity) && (
                              <span className="text-xs text-muted-foreground">
                                {item.quantity}x {formatCurrency(Number(item.unit_price))}
                              </span>
                            )}
                          </div>
                          {item.notes && (
                            <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {item.notes}
                            </p>
                          )}
                        </div>
                        <span className="text-sm font-semibold text-muted-foreground shrink-0">
                          {formatCurrency(Number(item.total_price))}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {/* New Items */}
              {items.length > 0 && (
                <>
                  {sentItems.length > 0 && (
                    <div className="px-3 py-2 bg-primary/10">
                      <div className="flex items-center gap-2 text-xs font-medium text-primary">
                        <Plus className="h-3 w-3" />
                        Novos ({items.length})
                      </div>
                    </div>
                  )}
                  {items.map((item) => (
                    <div key={item.product.id} className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.product.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {onUpdateCartQuantity && (
                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => onUpdateCartQuantity(item.product.id, -1)}
                                  disabled={isSubmitting}
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                                <span className="w-6 text-center text-sm font-medium">
                                  {item.quantity}
                                </span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => onUpdateCartQuantity(item.product.id, 1)}
                                  disabled={isSubmitting}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                            {!onUpdateCartQuantity && (
                              <span className="text-xs text-muted-foreground">
                                {item.quantity}x {formatCurrency(Number(item.product.price))}
                              </span>
                            )}
                          </div>
                          {item.notes && (
                            <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {item.notes}
                            </p>
                          )}
                        </div>
                        <span className="text-sm font-semibold shrink-0">
                          {formatCurrency(Number(item.product.price) * item.quantity)}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {/* Cancelled Items */}
              {cancelledItems && cancelledItems.length > 0 && (
                <>
                  <div className="px-3 py-2 bg-destructive/10">
                    <div className="flex items-center gap-2 text-xs font-medium text-destructive">
                      <XCircle className="h-3 w-3" />
                      Cancelados ({cancelledItems.length})
                    </div>
                  </div>
                  {cancelledItems.map((item) => (
                    <div key={item.id} className="p-3 bg-destructive/5 opacity-60">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate line-through text-muted-foreground">
                            {item.product_name}
                          </p>
                          <span className="text-xs text-muted-foreground">
                            {item.quantity}x {formatCurrency(Number(item.unit_price))}
                          </span>
                          {item.reason && (
                            <p className="text-xs text-destructive mt-0.5">
                              Motivo: {item.reason}
                            </p>
                          )}
                        </div>
                        <span className="text-sm font-semibold text-muted-foreground shrink-0 line-through">
                          {formatCurrency(Number(item.total_price))}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Empty State */}
              {items.length === 0 && sentItems.length === 0 && (
                <div className="p-6 text-center text-muted-foreground text-sm">
                  Nenhum item no pedido
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Middle Column - Payment Methods */}
        <div className="flex-1 border-r overflow-auto">
          <ScrollArea className="h-full">
            <div className="p-4 space-y-6">
              {/* Pagamentos Adicionados */}
              {payments.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-base">Pagamentos Adicionados</Label>
                  <div className="space-y-2">
                    {payments.map((payment) => {
                      const pmColor = payment.paymentMethod.icon_color;
                      const pmContrast = pmColor ? getContrastColor(pmColor) : undefined;
                      return (
                        <div
                          key={payment.id}
                          className={cn("flex items-center justify-between p-3 rounded-lg", !pmColor && "bg-muted")}
                          style={pmColor ? { backgroundColor: pmColor, color: pmContrast } : undefined}
                        >
                          <div className="flex items-center gap-2" style={pmColor ? { color: pmContrast } : undefined}>
                            {pmColor ? (
                              <span style={{ color: pmContrast }}>{paymentTypeIcons[payment.paymentMethod.type]}</span>
                            ) : paymentTypeIcons[payment.paymentMethod.type]}
                            <span className="font-medium">{payment.paymentMethod.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{formatCurrency(payment.amount)}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleRemovePayment(payment.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Troco */}
              {changeAmount > 0.009 && payments.length > 0 && (
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-blue-700 dark:text-blue-400">
                      Troco
                    </span>
                    <span className="text-xl font-bold text-blue-700 dark:text-blue-400">
                      {formatCurrency(changeAmount)}
                    </span>
                  </div>
                </div>
              )}

              {/* Falta pagar */}
              {remainingAmount > 0.01 && (
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-amber-700 dark:text-amber-400">
                      Falta pagar
                    </span>
                    <span className="text-xl font-bold text-amber-700 dark:text-amber-400">
                      {formatCurrency(remainingAmount)}
                    </span>
                  </div>
                </div>
              )}

              {/* Adicionar Pagamento */}
              {remainingAmount > 0.01 && (
                <div className="space-y-3 p-4 rounded-lg border">
                  <Label className="text-base">Adicionar Pagamento</Label>
                  {isLoadingPayments ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                      {activePaymentMethods.map((method) => {
                        const hasColor = !!method.icon_color;
                        const contrastColor = hasColor ? getContrastColor(method.icon_color!) : undefined;
                        return (
                          <button
                            key={method.id}
                            type="button"
                            onClick={() => handleSelectPaymentMethod(method)}
                            className={cn(
                              "flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left",
                              newPaymentMethod?.id === method.id
                                ? "border-primary ring-2 ring-primary/30"
                                : hasColor ? "border-transparent hover:opacity-90" : "border-muted hover:border-primary/50"
                            )}
                            style={hasColor ? { backgroundColor: method.icon_color!, color: contrastColor } : undefined}
                          >
                            <span style={hasColor ? { color: contrastColor } : undefined}>
                              {paymentTypeIcons[method.type]}
                            </span>
                            <span className="text-sm font-medium">{method.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div>
                    <NumericKeypadDock />
                  </div>

                  <div className="flex gap-2">
                    <Input
                      ref={paymentAmountInputRef}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Valor"
                      value={newPaymentAmount}
                      onChange={(e) => setNewPaymentAmount(e.target.value)}
                      onFocus={paymentAmountField.activate}
                      onClick={paymentAmountField.activate}
                      readOnly={paymentAmountField.readOnly}
                      inputMode={paymentAmountField.inputMode}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleFillRemaining}
                    >
                      Restante
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      onClick={handleAddPayment}
                      disabled={!canAddPayment}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Pagamento completo */}
              {remainingAmount <= 0.01 && payments.length > 0 && (
                <div className="space-y-3">
                  <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                    <p className="font-medium text-green-700 dark:text-green-400 text-center">
                      ✓ Pagamento completo - clique em Finalizar
                    </p>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Right Column - Summary & Options */}
        <div className="w-80 lg:w-96 flex flex-col bg-muted/30">
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-4">
              {/* Discount Section */}
              <div className="space-y-2">
                <Label>Desconto</Label>
                <div className="flex gap-2">
                  <div className="flex rounded-md border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setDiscountType("percentage")}
                      className={cn(
                        "px-3 py-2 flex items-center text-sm transition-colors",
                        discountType === "percentage"
                          ? "bg-primary text-primary-foreground"
                          : "bg-background hover:bg-muted"
                      )}
                    >
                      <Percent className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiscountType("fixed")}
                      className={cn(
                        "px-3 py-2 flex items-center text-sm transition-colors",
                        discountType === "fixed"
                          ? "bg-primary text-primary-foreground"
                          : "bg-background hover:bg-muted"
                      )}
                    >
                      <DollarSign className="h-4 w-4" />
                    </button>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    max={discountType === "percentage" ? "100" : undefined}
                    step={discountType === "percentage" ? "1" : "0.01"}
                    placeholder={discountType === "percentage" ? "0%" : "R$ 0,00"}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    onFocus={() => discountType === "fixed" && discountFixedField.activate()}
                    onClick={() => discountType === "fixed" && discountFixedField.activate()}
                    readOnly={discountType === "fixed" ? discountFixedField.readOnly : undefined}
                    inputMode={discountType === "fixed" ? discountFixedField.inputMode : undefined}
                    className="flex-1"
                  />
                </div>
              </div>

              {/* People Count */}
              {peopleCountCheckoutEnabled && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Número de Pessoas
                  </Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setPeopleCount(Math.max(0, peopleCount - 1))}
                      disabled={peopleCount <= 0}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      type="number"
                      min="0"
                      value={peopleCount || ""}
                      onChange={(e) => setPeopleCount(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-16 text-center"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setPeopleCount(peopleCount + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Taxas e Adicionais */}
              {(serviceTaxEnabled || couvertEnabled) && (
                <div className="space-y-3 rounded-lg border p-4 bg-card">
                  <Label className="text-base font-semibold">Taxas e Adicionais</Label>
                  
                  {/* Taxa de Serviço */}
                  {serviceTaxEnabled && (
                    <div className="space-y-2 pt-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={applyServiceTax}
                            onCheckedChange={setApplyServiceTax}
                          />
                          <span className="font-medium">Taxa de Serviço</span>
                        </div>
                        <span className={cn(
                          "font-semibold",
                          !applyServiceTax && "text-muted-foreground line-through"
                        )}>
                          +{formatCurrency(serviceTaxAmount)}
                        </span>
                      </div>
                      {applyServiceTax && (
                        <div className="flex items-center gap-2 pl-10">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={customServiceTaxPercentage}
                            onChange={(e) => setCustomServiceTaxPercentage(e.target.value)}
                            className="w-20 h-8 text-sm"
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Couvert */}
                  {couvertEnabled && (
                    <div className="space-y-2 pt-2 border-t">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={applyCouvert}
                            onCheckedChange={setApplyCouvert}
                          />
                          <span className="font-medium">Couvert</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatCurrency(couvertPrice)}/pessoa)
                          </span>
                        </div>
                        <span className={cn(
                          "font-semibold",
                          !applyCouvert && "text-muted-foreground line-through"
                        )}>
                          +{formatCurrency(couvertAmount)}
                        </span>
                      </div>
                      {applyCouvert && (
                        <div className="flex items-center gap-2 pl-10">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setCouvertQuantity(Math.max(0, couvertQuantity - 1))}
                            disabled={couvertQuantity <= 0}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <Input
                            type="number"
                            min="0"
                            value={couvertQuantity || ""}
                            onChange={(e) => setCouvertQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                            className="w-16 h-8 text-center text-sm"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setCouvertQuantity(couvertQuantity + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <span className="text-sm text-muted-foreground">pessoas</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea
                  placeholder="Observações..."
                  value={orderInfo.notes || ""}
                  onChange={(e) =>
                    onOrderInfoChange({ ...orderInfo, notes: e.target.value })
                  }
                  className="resize-none"
                  rows={2}
                />
              </div>

              {/* Summary Card */}
              <div className="rounded-lg bg-card border p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Itens</span>
                  <span>{itemCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Desconto</span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                {serviceTaxEnabled && applyServiceTax && serviceTaxAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Taxa de Serviço ({effectiveServiceTaxPercentage}%)</span>
                    <span>+{formatCurrency(serviceTaxAmount)}</span>
                  </div>
                )}
                {couvertEnabled && applyCouvert && couvertQuantity > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Couvert ({couvertQuantity}x)</span>
                    <span>+{formatCurrency(couvertAmount)}</span>
                  </div>
                )}
                {/* Entry Info */}
                {commandEntry && (
                  <div className="space-y-1 pt-2 border-t border-dashed">
                    <div className="flex items-center gap-2 text-sm">
                      <Ticket className="h-4 w-4 text-primary" />
                      <span className="font-medium text-primary">{commandEntry.entry_type_name}</span>
                    </div>
                    {commandEntry.entry_mode === "fixed" && entryAmount > 0 && (
                      <div className="flex justify-between text-sm text-primary font-medium">
                        <span>Taxa de entrada</span>
                        <span>+{formatCurrency(entryAmount)}</span>
                      </div>
                    )}
                    {commandEntry.entry_mode === "minimum_consumption" && entryAmount > 0 && (
                      <div className="flex justify-between text-sm text-amber-600 font-medium">
                        <span>Diferença consumação</span>
                        <span>+{formatCurrency(entryAmount)}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-between font-bold text-xl pt-2 border-t">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
                {peopleCount > 1 && (
                  <div className="flex justify-between text-sm text-muted-foreground pt-1">
                    <span>Valor por pessoa ({peopleCount}x)</span>
                    <span>{formatCurrency(total / peopleCount)}</span>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>

          {/* Action Buttons */}
          <div className="p-4 border-t bg-card space-y-2">
            {/* Partial Payments Summary */}
            {partialPayments.length > 0 && (
              <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 mb-3">
                <div className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400 mb-2">
                  <Receipt className="h-4 w-4" />
                  Pagamentos Parciais
                </div>
                <div className="space-y-1">
                  {partialPayments.map((pp) => (
                    <div key={pp.id} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {pp.payer_name || "Anônimo"} ({pp.payment_method?.name})
                      </span>
                      <span className="text-green-600 font-medium">
                        -{formatCurrency(pp.amount)}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold pt-1 border-t border-green-200 dark:border-green-800">
                    <span>Total Pago</span>
                    <span className="text-green-600">-{formatCurrency(partialTotalPaid)}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Partial Payment Button */}
            {isPartialPaymentEnabled && orderId && amountToPayFinal > 0 && (
              <Button
                variant="outline"
                className="w-full h-10 gap-2"
                onClick={() => setPartialPaymentDialogOpen(true)}
                disabled={isSubmitting}
              >
                <Receipt className="h-4 w-4" />
                Pagamento Parcial
              </Button>
            )}
            
            {/* Fiscal button - only shown when ACBr is configured and callback is provided */}
            {fiscalEnabled && onNfceEmission && (
              <Button
                className="w-full h-12 text-lg gap-2 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleConfirmWithNfce}
                disabled={!isPaymentValid || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Finalizando...
                  </>
                ) : (
                  <>
                    <FileText className="h-5 w-5" />
                    Finalizar e Emitir NFC-e
                  </>
                )}
              </Button>
            )}
            
            {/* Standard finalize button */}
            <Button
              variant={fiscalEnabled && onNfceEmission ? "outline" : "default"}
              className="w-full h-12 text-lg"
              onClick={() => handleConfirm()}
              disabled={!isPaymentValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Finalizando...
                </>
              ) : (
                `Finalizar ${formatCurrency(amountToPayFinal)}`
              )}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={onBack}
              disabled={isSubmitting}
            >
              Voltar
            </Button>
          </div>
        </div>

        {/* Keypad Dock movido para dentro do card de pagamento */}
      </div>

      {/* Partial Payment Dialog */}
      {orderId && orderNumber && (
        <PartialPaymentDialog
          open={partialPaymentDialogOpen}
          onOpenChange={setPartialPaymentDialogOpen}
          orderId={orderId}
          orderNumber={orderNumber}
          tableNumber={orderInfo.tableNumber}
          tabNumber={orderInfo.tabNumber}
          remainingAmount={amountToPayFinal}
          onSuccess={refetchPartialPayments}
        />
      )}

      {/* Decrease Confirmation Dialog */}
      <AlertDialog 
        open={decreaseConfirmDialog.open} 
        onOpenChange={(open) => !isDecreasing && setDecreaseConfirmDialog({ open, item: open ? decreaseConfirmDialog.item : null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirmar Redução de Quantidade
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Ao diminuir a quantidade de um item já enviado, será registrado um <strong>cancelamento parcial</strong> nos relatórios.
                </p>
                {decreaseConfirmDialog.item && (
                  <div className="bg-muted rounded-lg p-3 space-y-1">
                    <p className="font-medium text-foreground">{decreaseConfirmDialog.item.product_name}</p>
                    <p className="text-sm">
                      Quantidade atual: {decreaseConfirmDialog.item.quantity} → Nova: {decreaseConfirmDialog.item.quantity - 1}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Valor a cancelar: {formatCurrency(Number(decreaseConfirmDialog.item.unit_price))}
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDecreasing}>
              Voltar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDecrease}
              disabled={isDecreasing}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {isDecreasing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                "Confirmar Redução"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
  );
}
